import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEOHead from "@/components/SEOHead";
import BlogHeader from "@/components/blog/BlogHeader";
import BlogFooter from "@/components/blog/BlogFooter";

const Privacidade = () => {
  return (
    <>
      <SEOHead
        title="Política de Privacidade | Magnet Link"
        description="Saiba como o Magnet Link protege seus dados pessoais. Nossa política de privacidade em conformidade com a LGPD e melhores práticas de segurança."
        keywords="privacidade, LGPD, dados pessoais, proteção de dados, segurança"
        canonicalUrl="/privacidade"
      />

      <div className="min-h-screen bg-background">
        <BlogHeader />

        {/* Header */}
        <header className="pt-32 pb-12 md:pt-40 md:pb-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <nav 
              className="mb-8"
              aria-label="Navegação de retorno"
            >
              <Button variant="ghost" size="sm" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Voltar ao Início
                </Link>
              </Button>
            </nav>

            <div className="mx-auto max-w-4xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Shield className="h-6 w-6 text-primary" aria-hidden="true" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
                  Política de Privacidade
                </h1>
              </div>
              <p className="text-lg text-muted-foreground">
                Última atualização: Janeiro de 2025
              </p>
            </div>
          </div>
        </header>

        {/* Content */}
        <main role="main" aria-label="Política de Privacidade">
          <article className="py-12 md:py-16">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-4xl prose prose-gray dark:prose-invert">
                
                {/* Introdução */}
                <section aria-labelledby="intro" className="mb-12">
                  <h2 id="intro" className="text-2xl font-bold mb-4">1. Introdução</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    O Magnet Link ("nós", "nosso" ou "empresa") está comprometido em proteger a privacidade 
                    e os dados pessoais de nossos usuários. Esta Política de Privacidade explica como 
                    coletamos, usamos, armazenamos e protegemos suas informações quando você utiliza 
                    nossa plataforma de formulários inteligentes.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Ao utilizar o Magnet Link, você concorda com as práticas descritas nesta política. 
                    Recomendamos a leitura atenta deste documento.
                  </p>
                </section>

                {/* Cards com principais pontos */}
                <section className="mb-12 not-prose">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <Database className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Dados que Coletamos</h3>
                        <p className="text-sm text-muted-foreground">
                          Nome, email, telefone e informações fornecidas em formulários.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <Lock className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Segurança</h3>
                        <p className="text-sm text-muted-foreground">
                          Criptografia de ponta a ponta e armazenamento seguro na nuvem.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <Eye className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Transparência</h3>
                        <p className="text-sm text-muted-foreground">
                          Você pode solicitar acesso, correção ou exclusão de seus dados.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-border/50">
                      <CardContent className="p-5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 mb-3">
                          <UserCheck className="h-5 w-5 text-primary" aria-hidden="true" />
                        </div>
                        <h3 className="font-semibold mb-2">Seus Direitos</h3>
                        <p className="text-sm text-muted-foreground">
                          Conformidade total com a LGPD e proteção dos seus direitos.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </section>

                {/* Coleta de Dados */}
                <section aria-labelledby="coleta" className="mb-12">
                  <h2 id="coleta" className="text-2xl font-bold mb-4">2. Dados que Coletamos</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Coletamos os seguintes tipos de informações:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                    <li><strong>Dados de cadastro:</strong> nome, email, senha (criptografada)</li>
                    <li><strong>Dados de formulários:</strong> respostas fornecidas pelos leads em seus formulários</li>
                    <li><strong>Dados de uso:</strong> informações sobre como você utiliza a plataforma</li>
                    <li><strong>Dados técnicos:</strong> endereço IP, tipo de navegador, dispositivo</li>
                  </ul>
                </section>

                {/* Uso dos Dados */}
                <section aria-labelledby="uso" className="mb-12">
                  <h2 id="uso" className="text-2xl font-bold mb-4">3. Como Usamos seus Dados</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Utilizamos suas informações para:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                    <li>Fornecer e melhorar nossos serviços</li>
                    <li>Processar seus formulários e gerar PDFs personalizados</li>
                    <li>Enviar comunicações importantes sobre sua conta</li>
                    <li>Personalizar sua experiência na plataforma</li>
                    <li>Garantir a segurança e prevenir fraudes</li>
                  </ul>
                </section>

                {/* Compartilhamento */}
                <section aria-labelledby="compartilhamento" className="mb-12">
                  <h2 id="compartilhamento" className="text-2xl font-bold mb-4">4. Compartilhamento de Dados</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    <strong>Não vendemos seus dados.</strong> Compartilhamos informações apenas:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                    <li>Com provedores de serviços essenciais (hospedagem, processamento de pagamentos)</li>
                    <li>Quando exigido por lei ou ordem judicial</li>
                    <li>Com seu consentimento explícito</li>
                  </ul>
                </section>

                {/* Segurança */}
                <section aria-labelledby="seguranca" className="mb-12">
                  <h2 id="seguranca" className="text-2xl font-bold mb-4">5. Segurança dos Dados</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Implementamos medidas técnicas e organizacionais para proteger seus dados:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                    <li>Criptografia SSL/TLS em todas as comunicações</li>
                    <li>Criptografia de dados em repouso</li>
                    <li>Controles de acesso rigorosos</li>
                    <li>Monitoramento contínuo de segurança</li>
                    <li>Backups regulares e redundância de dados</li>
                  </ul>
                </section>

                {/* Seus Direitos */}
                <section aria-labelledby="direitos" className="mb-12">
                  <h2 id="direitos" className="text-2xl font-bold mb-4">6. Seus Direitos (LGPD)</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem direito a:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                    <li>Confirmar a existência de tratamento de seus dados</li>
                    <li>Acessar seus dados pessoais</li>
                    <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
                    <li>Solicitar a exclusão de seus dados</li>
                    <li>Revogar o consentimento a qualquer momento</li>
                    <li>Solicitar a portabilidade dos dados</li>
                  </ul>
                </section>

                {/* Contato */}
                <section aria-labelledby="contato" className="mb-12 not-prose">
                  <h2 id="contato" className="text-2xl font-bold mb-4">7. Contato</h2>
                  <Card className="bg-primary/5 border-primary/20">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Mail className="h-5 w-5 text-primary" aria-hidden="true" />
                        <span className="font-semibold">Dúvidas sobre privacidade?</span>
                      </div>
                      <p className="text-muted-foreground mb-4">
                        Para exercer seus direitos ou esclarecer dúvidas sobre esta política, 
                        entre em contato conosco:
                      </p>
                      <p className="text-muted-foreground">
                        <strong>Email:</strong> privacidade@magnetlink.com.br
                      </p>
                    </CardContent>
                  </Card>
                </section>

              </div>
            </div>
          </article>
        </main>

        <BlogFooter />
      </div>
    </>
  );
};

export default Privacidade;
